import React, { useState } from "react";
import { Handshake, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextField, AreaField, SelectField } from "@/components/forms/Field";
import { US_STATES } from "@/data/site";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["Broker", "Wholesaler", "Real Estate Agent", "Acquisition Company", "Investment Group", "Title Company", "Other"];

export default function BecomePartner() {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (n, v) => setF((s) => ({ ...s, [n]: v }));

  const submit = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/referrals", {
        name: f.name, company: f.company || "", email: f.email, phone: f.phone || "",
        partner_type: f.partner_type || "", state: f.state || "", monthly_volume: f.monthly_volume || "", message: f.message || "",
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 mb-5">
        <Handshake className="w-4 h-4 text-gold" /><span className="eyebrow">Referral Partners</span>
      </div>
      <h1 className="h-display text-4xl sm:text-5xl text-white">Have clients or deals that need capital?</h1>
      <p className="mt-3 text-dim">Partner with BullsEye Capital and give your clients access to financing across multiple strategies.</p>

      {done ? (
        <div className="mt-10 card-surface rounded-2xl p-10 text-center" data-testid="partner-success">
          <CheckCircle2 className="w-12 h-12 text-ok mx-auto" />
          <h3 className="h-display text-2xl text-white mt-4">Application Received</h3>
          <p className="mt-2 text-dim">Thanks for your interest. A BullsEye Capital representative will reach out.</p>
        </div>
      ) : (
        <div className="mt-10 card-surface rounded-2xl p-7 sm:p-8" data-testid="partner-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <TextField label="Name" name="name" value={f.name} onChange={set} required testId="partner-name" />
            <TextField label="Company" name="company" value={f.company} onChange={set} testId="partner-company" />
            <TextField label="Email" name="email" type="email" value={f.email} onChange={set} required testId="partner-email" />
            <TextField label="Phone" name="phone" value={f.phone} onChange={set} testId="partner-phone" />
            <SelectField label="Partner Type" name="partner_type" value={f.partner_type} onChange={set} options={TYPES} testId="partner-type" />
            <SelectField label="State" name="state" value={f.state} onChange={set} options={US_STATES} testId="partner-state" />
            <TextField label="Estimated Monthly Deal Volume" name="monthly_volume" value={f.monthly_volume} onChange={set} testId="partner-volume" className="sm:col-span-2" />
            <AreaField label="Message" name="message" value={f.message} onChange={set} testId="partner-message" />
          </div>
          <Button onClick={submit} disabled={busy} className="btn-gold rounded-full h-14 px-8 mt-6 gap-2" data-testid="partner-submit">
            {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : "Become a Partner"}
          </Button>
        </div>
      )}
    </div>
  );
}
